'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Input } from '@shared/components/ui/input';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEligibleStorageFilters } from '../../hooks/useEligibleStorageFilters';

interface EligibleStorageFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

/**
 * Panel de filtros del módulo Almacenamiento Elegible.
 */
export const EligibleStorageFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: EligibleStorageFiltersPanelProps) => {
  const { t } = useTranslation('eligibleStorageModule');

  const {
    filters,
    handleStorageDateRangeChange,
    handleQuantityMinChange,
    handleQuantityMaxChange,
    handlePreinvoiceFilterChange,
    handlePreinvoiceIdChange,
    handleInvoiceFilterChange,
    handleInvoiceIdChange,
    handlePeriodChange,
    clearAllFilters,
  } = useEligibleStorageFilters({ initialConditions, onChange });

  const preinvoiceOptions = useMemo(
    () => [
      { label: t('filters.preinvoice_empty'), value: 'is_null' },
      { label: t('filters.preinvoice_any'), value: 'is_not_null' },
      { label: t('filters.preinvoice_equals'), value: 'equals' },
    ],
    [t],
  );

  const invoiceOptions = useMemo(
    () => [
      { label: t('filters.invoice_empty'), value: 'is_null' },
      { label: t('filters.invoice_any'), value: 'is_not_null' },
      { label: t('filters.invoice_equals'), value: 'equals' },
    ],
    [t],
  );

  const periodOptions = useMemo(
    () => [
      { label: t('filters.periodOptions.weekly'), value: 'WEEKLY' },
      { label: t('filters.periodOptions.monthly'), value: 'MONTHLY' },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.storageDateRange.from || filters.storageDateRange.to) count++;
    if (filters.quantityMin.trim() || filters.quantityMax.trim()) count++;
    if (filters.preinvoiceFilter) count++;
    if (filters.invoiceFilter) count++;
    if (filters.period) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('table.date', 'Fecha')}>
          <DateRangePicker
            initialDateFrom={filters.storageDateRange.from}
            initialDateTo={filters.storageDateRange.to}
            onUpdate={({ range }) => handleStorageDateRangeChange(range)}
            placeholder={t('table.date', 'Fecha')}
            dataTestId="filter-storage-date"
            triggerClassName="h-[38px] text-sm"
          />
        </FilterField>

        <FilterField label={t('table.units', 'Unidades')}>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={filters.quantityMin}
              onChange={(e) => handleQuantityMinChange(e.target.value)}
              placeholder="Min"
              className="h-[38px]"
              data-testid="filter-quantity-min"
            />
            <span className="text-muted-foreground">-</span>
            <Input
              type="number"
              value={filters.quantityMax}
              onChange={(e) => handleQuantityMaxChange(e.target.value)}
              placeholder="Max"
              className="h-[38px]"
              data-testid="filter-quantity-max"
            />
          </div>
        </FilterField>

        <FilterField label={t('table.preinvoiceNumber', 'Nº de prefactura')}>
          <div className="space-y-2">
            <Combobox
              options={preinvoiceOptions}
              value={filters.preinvoiceFilter}
              setValue={handlePreinvoiceFilterChange}
              placeholder="Seleccionar..."
              dataTestId="filter-preinvoice-type"
            />
            {filters.preinvoiceFilter === 'equals' && (
              <Input
                type="number"
                value={filters.preinvoiceId}
                onChange={(e) => handlePreinvoiceIdChange(e.target.value)}
                placeholder="ID prefactura"
                className="h-[38px]"
                data-testid="filter-preinvoice-id"
              />
            )}
          </div>
        </FilterField>

        <FilterField label={t('table.invoiceNumber', 'Nº de factura')}>
          <div className="space-y-2">
            <Combobox
              options={invoiceOptions}
              value={filters.invoiceFilter}
              setValue={handleInvoiceFilterChange}
              placeholder="Seleccionar..."
              dataTestId="filter-invoice-type"
            />
            {filters.invoiceFilter === 'equals' && (
              <Input
                type="number"
                value={filters.invoiceId}
                onChange={(e) => handleInvoiceIdChange(e.target.value)}
                placeholder="ID factura"
                className="h-[38px]"
                data-testid="filter-invoice-id"
              />
            )}
          </div>
        </FilterField>

        <FilterField label={t('table.period', 'Período')}>
          <Combobox
            options={periodOptions}
            value={filters.period}
            setValue={handlePeriodChange}
            placeholder="Seleccionar..."
            dataTestId="filter-period"
          />
        </FilterField>
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="eligible-storage-clear-filters"
          >
            {t('filters.clear_filters', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
